import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ViewBtn from "../../Buttons/ViewBtn";
import { Select, Tag, Tooltip, message } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { loadAllTicketByCustomerId, loadAllTicketPaginated } from "../../../redux/rtk/features/crm/ticket/ticketSlice";
import { loadAllTicketStatus } from "../../../redux/rtk/features/crm/ticketStatus/ticketStatusSlice";
import getRoleFromToken from "../../../utils/getRoleFromToken";
import getUserFromToken from "../../../utils/getUserFromToken";
import CrmSearch from "../CommonUi/CrmSearch";
import CreateBtn from "./CreateBtn";
import TableComponent from "./TableComponent";
import EditPostModal from "./EditPostModal";
import axios from "axios";
import {
  IoSparklesOutline,
  IoCreateOutline,
  IoDocumentsOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoCloseCircleOutline,
  IoHourglassOutline,
} from "react-icons/io5";
import "./Ticket.css";

dayjs.extend(relativeTime);

// Group the configured post statuses into the buckets shown on the counter cards
const getStatusBucket = (statusName = "") => {
  const name = statusName.toLowerCase();
  if (name.includes("approve")) return "approved";
  if (name.includes("reject")) return "rejected";
  if (name.includes("pending")) return "pending";
  return "other";
};

// Counter tile shown inside the navy page header; clicking it filters the list
const PostStatCard = ({ icon: Icon, label, value, accent, hint, active, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className={`flex items-center gap-3 rounded-2xl p-4 text-left backdrop-blur-sm transition ${
      active
        ? "bg-white/25 ring-2 ring-white shadow-lg"
        : "bg-white/10 ring-1 ring-white/20 hover:bg-white/15"
    }`}
  >
    <div
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-lg`}
    >
      <Icon className='text-2xl' />
    </div>
    <div className='min-w-0'>
      <p className='text-2xl font-extrabold leading-none text-white'>{value}</p>
      <p className='mt-1 text-sm font-medium text-blue-50/90'>{label}</p>
      {hint && <p className='truncate text-xs text-blue-100/70'>{hint}</p>}
    </div>
  </button>
);

const Ticket = () => {
  const dispatch = useDispatch();
  const { Option } = Select;

  const { list: ticketList, loading, total } = useSelector((state) => state.ticket);
  const { list: crmTicketStatusList } = useSelector((state) => state.ticketStatus);

  const customerId = getUserFromToken() || null;
  const role = getRoleFromToken();

  const [usertype, setUsertype] = useState(null);
  const [editingTicket, setEditingTicket] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [stats, setStats] = useState(null);
  // "" = all, "bucket:<approved|pending|rejected|other>" from a counter tile, "id:<n>" from the dropdown
  const [statusFilter, setStatusFilter] = useState("");

  const statuses = stats?.statuses ||
    (crmTicketStatusList || []).map((s) => ({ id: s.id, name: s.ticketStatusName }));

  const getFilterStatusIds = (filter) => {
    if (filter.startsWith("id:")) return [Number(filter.slice(3))];
    if (filter.startsWith("bucket:")) {
      const bucket = filter.slice(7);
      const ids = statuses.filter((s) => getStatusBucket(s.name) === bucket).map((s) => s.id);
      // no status of that kind exists: match nothing instead of showing everything
      return ids.length ? ids : [-1];
    }
    return [];
  };
  const filterStatusIds = getFilterStatusIds(statusFilter);

  const loadStats = () =>
    axios
      .get(`ticket/stats`)
      .then(({ data }) => setStats(data))
      .catch((error) => console.error("Error fetching post stats:", error));

  const loadPosts = (ticketStatusId) => {
    const arg = { query: "all", ticketStatusId };
    if (role === "customer") {
      dispatch(loadAllTicketByCustomerId({ id: customerId, arg }));
    } else {
      dispatch(loadAllTicketPaginated(arg));
    }
  };

  const refreshList = () => {
    loadPosts(filterStatusIds);
    loadStats();
  };

  const applyStatusFilter = (filter) => {
    setStatusFilter(filter);
    loadPosts(getFilterStatusIds(filter));
  };
  // clicking the active tile again clears the filter
  const toggleBucket = (bucket) =>
    applyStatusFilter(statusFilter === `bucket:${bucket}` ? "" : `bucket:${bucket}`);

  const statusCounts = (stats?.statuses || []).reduce(
    (acc, status) => {
      const bucket = getStatusBucket(status.name);
      acc[bucket] += status.count;
      if (bucket === "other" && status.count) acc.otherNames.push(status.name);
      return acc;
    },
    { approved: 0, pending: 0, rejected: 0, other: 0, otherNames: [] }
  );

  const isApprovedStatus = (statusName = "") =>
    statusName.toLowerCase().includes("approve");

  const openEditModal = (record) => {
    if (isApprovedStatus(record?.ticketStatus?.ticketStatusName)) {
      message.warning("This post cannot be edited. It is approved now.");
      return;
    }
    setEditingTicket(record);
    setIsEditOpen(true);
  };

  useEffect(() => {
    const fetchUsertype = async () => {
      try {
        const response = await axios.get(`/customer/${customerId}`);
        setUsertype(response.data.usertype);
      } catch (error) {
        console.error("Error fetching usertype:", error);
      }
    };

    if (customerId) fetchUsertype();

    refreshList();

    dispatch(loadAllTicketStatus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, dispatch, role]);

  const columns = [
    {
      title: "ID",
      key: "ticketId",
      dataIndex: "ticketId",
    },
    {
      title: "Subject",
      key: "subject",
      dataIndex: "subject",
      width: "22%",
      render: (subject) => <span className='break-words'>{subject}</span>,
    },
    {
      title: "Post For",
      key: "postFor",
      dataIndex: "postFor",
      render: (postFor) => {
        let label;
        switch (postFor) {
          case 1:
            label = "Public";
            break;
          case 2:
            label = "Participants";
            break;
          case 3:
            label = "Faculty/Staff";
            break;
          default:
            label = "RSS Feeds"; // Optional default case
        }
        return <Tag color='blue'>{label}</Tag>;
      },
    },
    {
      title: "Post Owner Email",
      dataIndex: "email",
      key: "email",
      width: "20%",
      render: (email) => <span className='break-all'>{email}</span>,
    },
    {
      title: "Post Status",
      dataIndex: "ticketStatus",
      key: "ticketStatus",
      render: (ticketStatus) => <Tag color='red'>{ticketStatus?.ticketStatusName}</Tag>,
    },
    {
      title: "Post Owner Name",
      key: "customer",
      dataIndex: "customer",
      width: "20%",
      // Tags never wrap, so long names (e.g. full institute names) would spill into the next column
      render: (customer) => (
        <Tag color='green' className='whitespace-normal break-words leading-snug py-0.5 max-w-full'>
          {customer?.fullName}
        </Tag>
      ),
    },

    {
      title: "Action",
      dataIndex: "ticketId",
      key: "action",
      width: 110,
      render: (ticketId, record) => {
        const approved = isApprovedStatus(record?.ticketStatus?.ticketStatusName);
        return (
          <div className='flex justify-start items-center gap-2 action-btn-group'>
            <Tooltip title={approved ? "This post cannot be edited. It is approved now." : "Edit"}>
              <button
                type='button'
                className={`post-edit-btn${approved ? " is-disabled" : ""}`}
                onClick={() => openEditModal(record)}
              >
                <IoCreateOutline />
              </button>
            </Tooltip>
            {(role === "admin" || usertype === 1) && (
              <ViewBtn path={`/support/ticket/${ticketId}`} />
            )}
          </div>
        );
      },
    },
  ];
//   console.log("Role:", role, "Usertype:", usertype);

  return (
	<div className='customer-dashboard-page'>
	  <div className='customer-dashboard-header'>
		<div className='customer-dashboard-header-text'>
		  <span className='customer-dashboard-eyebrow'>
			<IoSparklesOutline /> Campus Pulse
		  </span>
		  <h2 className='customer-dashboard-title'>
			{role === "customer" ? "My Posts" : "All Posts"}
		  </h2>
		  <p className='customer-dashboard-subtitle'>
			{role === "customer"
			  ? usertype === 1
				? "Track and manage your posts and your department's posts."
				: "Track and manage everything you've submitted."
			  : "Browse and manage every post submitted across departments."}
		  </p>
		</div>
		<Link to='/support/ticket/create'>
		  <CreateBtn />
		</Link>

	  {stats && (
		<div
		  className={`w-full basis-full grid gap-3 grid-cols-2 ${
			statusCounts.other ? "lg:grid-cols-5" : "lg:grid-cols-4"
		  }`}
		>
		  <PostStatCard
			icon={IoDocumentsOutline}
			label={
			  role !== "customer"
				? "Total posts"
				: usertype === 1
				? "Department posts"
				: "My total posts"
			}
			value={stats.total}
			accent='from-[#023f88] to-[#0b5cae]'
			active={statusFilter === ""}
			onClick={() => applyStatusFilter("")}
		  />
		  <PostStatCard
			icon={IoCheckmarkCircleOutline}
			label='Approved'
			value={statusCounts.approved}
			accent='from-green-500 to-green-600'
			active={statusFilter === "bucket:approved"}
			onClick={() => toggleBucket("approved")}
		  />
		  <PostStatCard
			icon={IoTimeOutline}
			label='Pending'
			value={statusCounts.pending}
			accent='from-amber-500 to-orange-500'
			active={statusFilter === "bucket:pending"}
			onClick={() => toggleBucket("pending")}
		  />
		  <PostStatCard
			icon={IoCloseCircleOutline}
			label='Rejected'
			value={statusCounts.rejected}
			accent='from-rose-500 to-red-600'
			active={statusFilter === "bucket:rejected"}
			onClick={() => toggleBucket("rejected")}
		  />
		  {statusCounts.other > 0 && (
			<PostStatCard
			  icon={IoHourglassOutline}
			  label='Other statuses'
			  value={statusCounts.other}
			  hint={statusCounts.otherNames.join(", ")}
			  accent='from-indigo-500 to-violet-600'
			  active={statusFilter === "bucket:other"}
			  onClick={() => toggleBucket("other")}
			/>
		  )}
		</div>
	  )}
	  </div>

	  {usertype !== null && (
		<div className='customer-dashboard-toolbar'>
		  <div className='flex flex-wrap justify-end items-center gap-3'>
			<CrmSearch paginatedThunk={loadAllTicketPaginated} />
			<Select
			  value={statusFilter}
			  onChange={applyStatusFilter}
			  className='w-[220px]'
			  style={{ width: 220 }}
			  dropdownMatchSelectWidth={false}
			>
			  <Option value=''>All statuses</Option>
			  {statusFilter.startsWith("bucket:") && (
				<Option value={statusFilter}>
				  {{ approved: "Approved", pending: "Pending", rejected: "Rejected", other: "Other statuses" }[
					statusFilter.slice(7)
				  ]}
				</Option>
			  )}
			  {statuses.map((status) => (
				<Option key={status.id} value={`id:${status.id}`}>
				  <span className='capitalize'>{status.name}</span>
				  {status.count !== undefined && (
					<span className='ml-1 text-slate-500'>({status.count})</span>
				  )}
				</Option>
			  ))}
			</Select>
		  </div>
		</div>
	  )}

	  {usertype !== null && (
		<div className='customer-dashboard-table-card'>
		  <TableComponent
			total={total}
			role={role}
			customerId={customerId}
			paginatedThunk={loadAllTicketPaginated}
			filterArgs={{ ticketStatusId: filterStatusIds }}
			filterKey={statusFilter}
			columns={columns}
			list={ticketList}
			loading={loading}
			csvFileName={"Contact-List"}
		  />
		</div>
	  )}

	  <EditPostModal
		ticket={editingTicket}
		open={isEditOpen}
		onClose={() => setIsEditOpen(false)}
		onSaved={refreshList}
	  />
	</div>
  );

};

export default Ticket;
